<template>
  <div v-if="shouldDisplay" class="premium-concept-desc">
    <!-- Premium Slider -->
    <div v-if="patternImages.length > 0" class="premium-slider-container">
      <div class="concept-title-lockup">
        <span class="concept-title-rule" aria-hidden="true"></span>
        <TitleLogo class="concept-title-logo" :title="getConceptTitle()" :warning="false" />
        <div class="concept-title-tail">
          <span class="concept-title-rule" aria-hidden="true"></span>
          <span class="brand-font concept-selection-label">CONCEPT SELECTION</span>
        </div>
      </div>

      <!-- Main Slider -->
      <div class="slider-wrapper">
        <div ref="scrollContainer" class="photo-slider">
          <div v-for="(image, idx) in patternImages" :key="idx" class="photo-slide">
            <img :src="image.url" :alt="`Pattern ${String.fromCharCode(65 + idx)}`" class="photo-image" />
          </div>
        </div>

        <!-- Swipe Hint Ultra-Slim Bar -->
        <div v-if="patternImages.length > 1 && currentSlide === 0" class="swipe-hint-wrapper">
          <div class="swipe-hint-bar"></div>
        </div>
      </div>

      <!-- Pattern Selection Tabs -->
      <div ref="tabsContainer" :class="['pattern-tabs', { 'is-compact': patternImages.length <= 3 }]">
        <button
          v-for="(image, idx) in patternImages"
          :key="idx"
          type="button"
          @click="scrollTo(idx)"
          :class="['pattern-tab', { active: currentSlide === idx }]"
          :aria-pressed="currentSlide === idx ? 'true' : 'false'"
          :aria-label="String.fromCharCode(65 + idx) + ' ' + (lang === 'en' ? 'Pattern' : lang === 'jp' ? 'パターン' : '패턴')"
        >
          <span class="pattern-thumbnail">
            <img :src="image.url" alt="" loading="eager" decoding="async" />
            <span class="pattern-letter">{{ String.fromCharCode(65 + idx) }}</span>
            <span class="pattern-name">{{ lang === "en" ? "Pattern" : lang === "jp" ? "パターン" : "패턴" }}</span>
          </span>
        </button>
      </div>
    </div>
    <div ref="sampleAlbumHost" data-lafia-sample-album-host hidden></div>

    <!-- Storytelling Section -->
    <div v-if="storyModel.mode !== 'legacy' ? Boolean(storyModel.body.trim()) : descData.title" class="storytelling-section">
      <div v-if="storyModel.mode !== 'legacy'" ref="sharedStory"></div>
      <div v-else class="glossy-card">
        <!-- Title -->
        <h2 v-if="descData.title" class="brand-font title-text" v-html="formatText(descData.title)"></h2>

        <div class="content-space">
          <!-- SubTitle -->
          <p v-if="descData.subTitle" class="subtitle-text fade-up">{{ descData.subTitle }}</p>

          <div v-if="descData.subTitle && descData.storyLine" class="divider"></div>

          <!-- StoryLine -->
          <p v-if="descData.storyLine" class="storyline-text fade-up">{{ descData.storyLine }}</p>

          <!-- Closing -->
          <div v-if="descData.closing" class="closing-box fade-up">"{{ descData.closing }}"</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"
import TitleLogo from "@/views/components/TitleLogo"
const ConceptStory = require("../../../../../shared/concept-story.js")

export default {
  name: "PremiumConceptDesc",
  components: {
    TitleLogo,
  },
  props: {
    concept: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      currentSlide: 0,
      touchStartX: 0,
      touchStartY: 0,
      touchCurrentX: 0,
      touchCurrentY: 0,
      isDragging: false,
      isHorizontalSwipe: null,
      isMouseDown: false,
      albumCleanup: null,
      albumRequest: 0,
    }
  },

  computed: {
    ...mapState(["lang"]),

    storyModel() {
      return ConceptStory.select(this.concept?.concept_desc, this.lang)
    },

    shouldDisplay() {
      return this.concept?.productType === "premium"
    },

    descData() {
      if (!this.concept?.concept_desc) {
        return {
          title: "",
          subTitle: "",
          storyLine: "",
          closing: "",
        }
      }

      const conceptDesc = this.concept.concept_desc
      const langKey = this.lang || "kr"

      return {
        title: conceptDesc.title?.[langKey] || conceptDesc.title?.kr || "",
        subTitle: conceptDesc.subTitle?.[langKey] || conceptDesc.subTitle?.kr || "",
        storyLine: conceptDesc.storyLine?.[langKey] || conceptDesc.storyLine?.kr || "",
        closing: conceptDesc.closing?.[langKey] || conceptDesc.closing?.kr || "",
      }
    },

    patternImages() {
      const images = []

      // 기본 패턴 이미지 추가
      if (this.concept?.pattern_1_image) {
        images.push({
          url: `/upload/concept/thumbnail_image/${this.concept.pattern_1_image}`,
          desc: "",
        })
      }

      // 추가 패턴 이미지들 추가
      if (Array.isArray(this.concept?.pattern_others_image)) {
        this.concept.pattern_others_image.forEach((pattern) => {
          if (pattern.image) {
            images.push({
              url: `/upload/concept/thumbnail_image/${pattern.image}`,
              desc: "",
            })
          }
        })
      }

      return images
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.renderSharedStory()
      this.observeFadeElements()
      this.initSwipeHandlers()
      this.mountSampleAlbums()
    })
  },
  watch: {
    storyModel: {
      deep: true,
      handler() { this.$nextTick(this.renderSharedStory) },
    },
    concept() { this.$nextTick(this.mountSampleAlbums) },
    lang() { this.$nextTick(this.mountSampleAlbums) },
  },

  beforeDestroy() {
    this.albumRequest += 1
    if (this.albumCleanup) this.albumCleanup()
    this.removeSwipeHandlers()
  },

  methods: {
    mountSampleAlbums() {
      this.albumRequest += 1
      const request = this.albumRequest
      if (this.albumCleanup) this.albumCleanup()
      this.albumCleanup = null
      const host = this.$refs.sampleAlbumHost
      if (!host || !window.mountConceptSampleAlbums) return
      window.mountConceptSampleAlbums(host, {
        id: this.concept?.id,
        aliases: [this.concept?.title, this.concept?.title_en, this.concept?.title_jp],
        lang: this.lang,
      }).then((dispose) => {
        if (request !== this.albumRequest || !host.isConnected) {
          if (typeof dispose === "function") dispose()
        } else this.albumCleanup = dispose
      })
    },
    renderSharedStory() {
      if (this.$refs.sharedStory) ConceptStory.render(this.$refs.sharedStory, this.storyModel)
    },
    scrollTo(index) {
      this.currentSlide = index
      if (this.$refs.scrollContainer) {
        const container = this.$refs.scrollContainer
        const slideWidth = container.offsetWidth
        const offset = -index * slideWidth

        // 모든 슬라이드에 transform 적용
        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transform = `translateX(${offset}px)`
        })
      }

      // 탭 자동 스크롤
      this.$nextTick(() => {
        if (this.$refs.tabsContainer) {
          const tabsContainer = this.$refs.tabsContainer
          const activeTab = tabsContainer.children[index]
          if (activeTab) {
            const scrollLeft = activeTab.offsetLeft - tabsContainer.offsetWidth / 2 + activeTab.offsetWidth / 2
            tabsContainer.scrollTo({ left: scrollLeft, behavior: "smooth" })
          }
        }
      })
    },

    formatText(text) {
      if (!text) return ""
      // \n을 <br>로 변환
      return text.replace(/\n/g, "<br>")
    },

    observeFadeElements() {
      if (!this.$el || typeof this.$el.querySelectorAll !== "function") {
        return
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("visible")
            }
          })
        },
        { threshold: 0.1 }
      )

      const elements = this.$el.querySelectorAll(".fade-up")
      if (elements && elements.length > 0) {
        elements.forEach((el) => observer.observe(el))
      }
    },

    getConceptTitle() {
      if (!this.concept) return ""

      switch (this.lang) {
        case "en":
          return this.concept.title_en || this.concept.title
        case "jp":
          return this.concept.title_jp || this.concept.title
        default:
          return this.concept.title
      }
    },

    initSwipeHandlers() {
      const slider = this.$refs.scrollContainer
      if (!slider) return

      // Touch events
      slider.addEventListener("touchstart", this.handleTouchStart, { passive: true })
      slider.addEventListener("touchmove", this.handleTouchMove, { passive: false })
      slider.addEventListener("touchend", this.handleTouchEnd, { passive: true })

      // Mouse events for desktop
      slider.addEventListener("mousedown", this.handleMouseDown)
      slider.addEventListener("mousemove", this.handleMouseMove)
      slider.addEventListener("mouseup", this.handleMouseUp)
      slider.addEventListener("mouseleave", this.handleMouseLeave)
    },

    removeSwipeHandlers() {
      const slider = this.$refs.scrollContainer
      if (!slider) return

      slider.removeEventListener("touchstart", this.handleTouchStart)
      slider.removeEventListener("touchmove", this.handleTouchMove)
      slider.removeEventListener("touchend", this.handleTouchEnd)

      slider.removeEventListener("mousedown", this.handleMouseDown)
      slider.removeEventListener("mousemove", this.handleMouseMove)
      slider.removeEventListener("mouseup", this.handleMouseUp)
      slider.removeEventListener("mouseleave", this.handleMouseLeave)
    },

    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.touchStartY = e.touches[0].clientY
      this.touchCurrentX = this.touchStartX
      this.touchCurrentY = this.touchStartY
      this.isDragging = false
      this.isHorizontalSwipe = null
    },

    handleTouchMove(e) {
      if (!this.touchStartX || !this.touchStartY) return

      this.touchCurrentX = e.touches[0].clientX
      this.touchCurrentY = e.touches[0].clientY

      const diffX = Math.abs(this.touchCurrentX - this.touchStartX)
      const diffY = Math.abs(this.touchCurrentY - this.touchStartY)

      // 방향이 아직 결정되지 않았고, 충분한 이동이 있었다면 방향 결정
      if (this.isHorizontalSwipe === null && (diffX > 10 || diffY > 10)) {
        this.isHorizontalSwipe = diffX > diffY
      }

      // Horizontal swipe인 경우에만 기본 동작 막기
      if (this.isHorizontalSwipe) {
        e.preventDefault()
        this.isDragging = true

        // 실시간으로 슬라이드 이동 (visual feedback)
        const container = this.$refs.scrollContainer
        const slideWidth = container.offsetWidth
        const offset = this.touchCurrentX - this.touchStartX
        const currentOffset = -this.currentSlide * slideWidth

        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transition = "none"
          slide.style.transform = `translateX(${currentOffset + offset}px)`
        })
      }
      // Vertical swipe면 기본 스크롤 동작 허용
    },

    handleTouchEnd() {
      if (!this.isDragging || !this.isHorizontalSwipe) {
        this.resetTouch()
        return
      }

      const diffX = this.touchCurrentX - this.touchStartX
      const threshold = 50 // 최소 swipe 거리

      // 왼쪽으로 swipe (다음 슬라이드)
      if (diffX < -threshold && this.currentSlide < this.patternImages.length - 1) {
        this.scrollTo(this.currentSlide + 1)
      }
      // 오른쪽으로 swipe (이전 슬라이드)
      else if (diffX > threshold && this.currentSlide > 0) {
        this.scrollTo(this.currentSlide - 1)
      }
      // threshold 이하면 원위치
      else {
        this.scrollTo(this.currentSlide)
      }

      this.resetTouch()
    },

    resetTouch() {
      this.touchStartX = 0
      this.touchStartY = 0
      this.touchCurrentX = 0
      this.touchCurrentY = 0
      this.isDragging = false
      this.isHorizontalSwipe = null
      this.isMouseDown = false

      // transition 복구
      const container = this.$refs.scrollContainer
      if (container) {
        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transition = ""
        })
      }
    },

    handleMouseDown(e) {
      this.isMouseDown = true
      this.touchStartX = e.clientX
      this.touchCurrentX = this.touchStartX
      this.isDragging = false
      e.preventDefault()
    },

    handleMouseMove(e) {
      if (!this.isMouseDown) return

      this.touchCurrentX = e.clientX
      const diffX = Math.abs(this.touchCurrentX - this.touchStartX)

      if (diffX > 5) {
        this.isDragging = true

        // 실시간으로 슬라이드 이동
        const container = this.$refs.scrollContainer
        const slideWidth = container.offsetWidth
        const offset = this.touchCurrentX - this.touchStartX
        const currentOffset = -this.currentSlide * slideWidth

        const slides = container.querySelectorAll(".photo-slide")
        slides.forEach((slide) => {
          slide.style.transition = "none"
          slide.style.transform = `translateX(${currentOffset + offset}px)`
        })
      }
    },

    handleMouseUp() {
      if (!this.isMouseDown) return

      if (this.isDragging) {
        const diffX = this.touchCurrentX - this.touchStartX
        const threshold = 50

        if (diffX < -threshold && this.currentSlide < this.patternImages.length - 1) {
          this.scrollTo(this.currentSlide + 1)
        } else if (diffX > threshold && this.currentSlide > 0) {
          this.scrollTo(this.currentSlide - 1)
        } else {
          this.scrollTo(this.currentSlide)
        }
      }

      this.resetTouch()
    },

    handleMouseLeave() {
      if (this.isMouseDown && this.isDragging) {
        this.scrollTo(this.currentSlide)
        this.resetTouch()
      }
    },
  },
}
</script>

<style src="../../../../../shared/concept-story.css"></style>
<style scoped>
@import url("https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/variable/pretendardvariable.css");

.premium-concept-desc {
  font-family: "Pretendard Variable", sans-serif;
  width: 100%;
  margin-bottom: 40px;
}

.brand-font {
  font-family: "Cafe24Danjunghae", sans-serif;
}

/* Premium Slider */
.premium-slider-container {
  width: 100%;
  max-width: 640px;
  margin: 0 auto 16px;
  padding: 0 16px;
  position: relative;
}

/* Compact Concept Title */
.concept-title-lockup {
  display: grid;
  grid-template-columns: minmax(28px, 1fr) auto minmax(88px, 1fr);
  align-items: end;
  column-gap: 14px;
  margin: -18px 0 10px;
}

.concept-title-rule {
  display: block;
  width: 100%;
  height: 1px;
  margin-bottom: 20px;
  background: rgba(171, 128, 91, 0.72);
}

.concept-title-logo {
  min-width: 128px;
}

.concept-title-logo::v-deep .v-image {
  width: 88px !important;
  height: 40px !important;
  margin-top: 0 !important;
}

.concept-title-logo::v-deep p {
  margin-bottom: 0 !important;
  font-size: 26px !important;
  line-height: 1.15;
  white-space: nowrap;
}

.concept-title-tail {
  display: grid;
  gap: 5px;
  align-self: end;
  padding-bottom: 5px;
}

.concept-title-tail .concept-title-rule {
  margin-bottom: 0;
}

.concept-selection-label {
  color: #dc8d9f;
  font-size: 9px;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
}

.pattern-tabs {
  display: flex;
  gap: clamp(6px, 2vw, 9px);
  width: 100%;
  overflow-x: auto;
  margin-top: 10px;
  padding: 6px 16px 8px;
  scroll-behavior: smooth;
  scroll-padding-inline: 16px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.pattern-tabs.is-compact {
  justify-content: center;
}

.pattern-tabs::-webkit-scrollbar {
  display: none;
}

.pattern-tab {
  flex: 0 0 64px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  scroll-snap-align: start;
  transition: transform 0.2s ease;
}

.pattern-tab:hover {
  transform: translateY(-1px);
}

.pattern-tab:focus-visible {
  outline: 3px solid rgba(220, 141, 159, 0.45);
  outline-offset: 3px;
}

.pattern-thumbnail {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 72 / 95;
  overflow: hidden;
  border: 1px solid rgba(220, 141, 159, 0.2);
  border-radius: 8px;
  background: #f8eef1;
  box-shadow: 0 4px 12px rgba(94, 54, 63, 0.1);
}

.pattern-thumbnail::after {
  content: "";
  position: absolute;
  inset: 42% 0 0;
  background: linear-gradient(to bottom, transparent, rgba(30, 16, 20, 0.76));
  pointer-events: none;
}

.pattern-thumbnail img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pattern-tab.active .pattern-thumbnail {
  border-color: #dc8d9f;
  outline: 3px solid #dc8d9f;
  outline-offset: 1px;
  box-shadow: 0 7px 18px rgba(220, 141, 159, 0.28);
}

.pattern-letter {
  position: absolute;
  z-index: 1;
  left: 8px;
  bottom: 19px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  color: white;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.45);
}

.pattern-name {
  position: absolute;
  z-index: 1;
  left: 8px;
  right: 7px;
  bottom: 6px;
  overflow: hidden;
  color: white;
  font-size: 9px;
  font-weight: 500;
  line-height: 1;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}

/* Slider */
.slider-wrapper {
  position: relative;
}

.slider-wrapper:hover .photo-image {
  transform: scale(1.1);
}

.photo-slider {
  display: flex;
  overflow: hidden;
  position: relative;
  border-radius: 40px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  background-color: #f9fafb;
  touch-action: auto;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  cursor: grab;
}

.photo-slider:active {
  cursor: grabbing;
}

.photo-slide {
  width: 100%;
  flex-shrink: 0;
  position: relative;
  aspect-ratio: 4 / 5;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.photo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 2s ease;
}

/* Swipe Hint - Ultra-Slim Bar */
.swipe-hint-wrapper {
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 20;
  display: block;
}

.swipe-hint-bar {
  width: 6px;
  height: 96px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 0 10px rgba(0, 0, 0, 0.2),
    0 0 4px rgba(0, 0, 0, 0.1);
  animation: swipeHint 2s infinite ease-in-out;
}

@keyframes swipeHint {
  0%,
  100% {
    transform: translateX(0);
    opacity: 0.5;
  }
  50% {
    transform: translateX(-4px);
    opacity: 0.9;
  }
}

/* Storytelling Section */
.storytelling-section {
  width: 95%;
  max-width: 580px;
  margin: 0 auto;
  padding-bottom: 40px;
}

.glossy-card {
  padding: 32px;
  border-radius: 32px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}

.title-text {
  font-size: 1.6rem;
  color: #dc8d9f;
  margin-bottom: 24px;
  line-height: 1.3;
}

.content-space {
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: #555;
  line-height: 1.6;
  font-size: 1.05rem;
}

.subtitle-text,
.storyline-text {
  margin: 0;
}

.divider {
  width: 48px;
  height: 1px;
  background: rgba(220, 141, 159, 0.3);
  margin: 8px 0;
}

.closing-box {
  padding: 24px;
  background-color: #fdf6f8;
  border-radius: 16px;
  border: 1px solid #fceef3;
  font-size: 0.95rem;
  font-style: italic;
  color: #888;
}

/* Fade Animation */
.fade-up {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.fade-up.visible {
  opacity: 1;
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 768px) {
  .title-text {
    font-size: 1.3rem;
  }

  .glossy-card {
    padding: 24px;
  }

  .content-space {
    font-size: 1rem;
  }

  .concept-title-lockup {
    grid-template-columns: minmax(22px, 1fr) auto minmax(76px, 1fr);
    column-gap: 9px;
  }

  .pattern-tab {
    flex-basis: clamp(58px, 18vw, 64px);
  }

  .pattern-tabs.is-compact .pattern-tab {
    flex-basis: min(64px, calc((100% - 18px) / 3));
  }

  .pattern-letter {
    font-size: 18px;
  }

  .swipe-hint-wrapper {
    right: 2px;
  }

  .swipe-hint-bar {
    width: 5px;
    height: 80px;
  }
}
</style>
